/**
 * Common Zod parsers used across multiple validator files.
 * These are reusable building blocks for validation schemas.
 */
import { isValid, parseISO } from 'date-fns';
import { z } from 'zod';

/**
 * Validates a string that can be coerced to a finite, non-negative integer.
 * Useful for query parameters that come as strings but represent numbers.
 */
export const finitePositiveIntegerStringVal = z.coerce.number().int().nonnegative();

/**
 * Validates a number that is a finite, non-negative integer.
 * Use this when the input is already a number (e.g., from JSON body).
 */
export const finitePositiveInteger = z.number().int().nonnegative();

/**
 * Validates a string that can be coerced to a non-negative 32-bit integer.
 * Max value is 2147483647 (2^31 - 1), useful for database integer columns.
 */
export const positiveInteger32StringVal = z.coerce.number().int().nonnegative().max(2147483647);

/**
 * Validates a number that is a non-negative 32-bit integer.
 * Max value is 2147483647 (2^31 - 1), useful for database integer columns.
 */
export const positiveInteger32 = z.number().int().nonnegative().max(2147483647);

/**
 * Validates a string of decimal digits naming a positive 32-bit integer id, such as a route param.
 * Unlike the coercing parsers above, it rejects strings that `Number()` would accept, like '1e3', '0x10' or ' 12'.
 */
export const positiveIntegerIdStringVal = z.string().regex(/^[1-9]\d*$/).transform(Number).pipe(positiveInteger32);

/**
 * Parses a positive integer id from an untrusted value, such as a route param, or returns undefined.
 */
export const parsePositiveIntegerId = (value: unknown): number | undefined => {
  const result = positiveIntegerIdStringVal.safeParse(value);
  return result.success ? result.data : undefined;
};

/**
 * Validates a valid ISO 8601 date string.
 * Accepts both short form (YYYY-MM-DD) and full format (YYYY-MM-DDTHH:mm:ssZ).
 */
export const iso8601String = z.string().refine(
  (value) => isValid(parseISO(value)),
  { message: 'Invalid ISO 8601 date string' },
);

/**
 * Validates a non-empty string (min length 1 after trimming).
 */
export const nonEmptyString = z.string().min(1).refine((val) => val.trim().length >= 1, {
  message: 'String must contain at least 1 character',
});

/**
 * Validates a boolean-like value (boolean or string 'true'/'false') and transforms it to a boolean.
 * Accepts: true, false, 'true', 'false'
 */
export const booleanStringVal = z.union([
  z.boolean(),
  z.enum(['true', 'false']).transform((v) => v === 'true'),
]);
